import { DismissIcon, Flex, Typography } from '@iheartjane/reefer';
import { useQueryClient } from '@tanstack/react-query';
import kebabCase from 'lodash-es/kebabCase';
import { useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';

import {
  FilterAndSort,
  HeaderIcons,
  HeaderWithIcons,
  IconButton,
  MenuProducts,
  SignInButton,
  getColumnCount,
} from '@jane/kiosk/components';
import type { RootLoaderType } from '@jane/kiosk/data-access';
import { useKioskUser } from '@jane/kiosk/data-access';
import {
  useKioskCart,
  useKioskSpacing,
  useRowContext,
  useTypedRouteLoaderData,
} from '@jane/kiosk/hooks';
import { useMenuProductSearch } from '@jane/kiosk/providers';
import { useGetStore } from '@jane/shared/data-access';
import type { Store } from '@jane/shared/types';

export const FeaturedCategory = () => {
  const { pathname } = useLocation();
  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const queryClient = useQueryClient();

  const { data: store, isSuccess } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });

  const { data: kioskUser } = useKioskUser();
  const signedIn = !!kioskUser;
  const showSignIn =
    store?.kiosk_anonymous_checkout !== 'required' && store?.kiosk_allow_auth;
  const { data: cart } = useKioskCart();

  const navigate = useNavigate();
  const { rowId, slug, featuredTitle, featuredFilter } = useRowContext(store);
  const { defaultSpacing, getKioskSpacing, isIpad } = useKioskSpacing();
  const defaultSortName = store.optional_filters ? 'Featured' : null;
  const searchProps = useMenuProductSearch({
    category: 'featured',
    columnCount: getColumnCount(),
    defaultSortName,
    featuredCategory: featuredFilter,
    store,
  });

  const navigateToMenu = useCallback(() => {
    navigate(`/stores/${storeId}/menu/featured`, {
      state: { resetSearch: true },
    });
  }, [navigate, storeId]);

  const onSearchIconClick = useCallback(() => {
    if (rowId) {
      navigate(
        `/stores/${storeId}/menu/rows/${rowId}/${slug || kebabCase(featuredTitle)}/search`
      );
    } else {
      navigate(
        `/stores/${storeId}/menu/featured/${encodeURIComponent(
          kebabCase(featuredTitle)
        )}/search`
      );
    }
  }, [navigate, rowId, slug, featuredTitle, storeId]);

  const onLoginIconClick = useCallback(() => {
    navigate(`${pathname}/login`);
  }, [navigate, pathname]);

  const onLogoutIconClick = useCallback(() => {
    navigate(`${pathname}/logout`);
  }, [navigate, pathname]);

  const onCartButtonClick = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['cart'],
    });
    navigate(`/stores/${storeId}/cart`);
  }, [navigate, storeId]);

  return (
    <>
      <HeaderWithIcons signedIn={signedIn} sticky>
        <Flex flexDirection="column" width="100%">
          {showSignIn && !signedIn && (
            <SignInButton
              location="featured"
              onLoginIconClick={onLoginIconClick}
              ml="auto"
              mb={getKioskSpacing(32, 16)}
            />
          )}
          <Flex
            alignItems="center"
            flexDirection="row"
            mt={getKioskSpacing(12, 8)}
          >
            <IconButton
              ariaLabel="navigate back"
              icon={
                <DismissIcon
                  altText="dismiss icon"
                  size={isIpad ? 'lg' : 'xl'}
                />
              }
              onClick={navigateToMenu}
            />
            <Typography
              as="h3"
              maxLines={2}
              ml={defaultSpacing}
              variant={isIpad ? 'h2-regular' : 'h1-regular'}
              style={{ ...(isIpad && { fontWeight: 400 }) }}
            >
              {featuredTitle}
            </Typography>
            <Flex grow={1} justifyContent="flex-end">
              <HeaderIcons
                cart={cart}
                onCartButtonClick={onCartButtonClick}
                onLoginIconClick={onLoginIconClick}
                onLogoutIconClick={onLogoutIconClick}
                onSearchIconClick={onSearchIconClick}
                signedIn={signedIn}
                storeId={store.id}
              />
            </Flex>
          </Flex>
        </Flex>
      </HeaderWithIcons>
      {isSuccess && (
        <FilterAndSort
          defaultSortName={defaultSortName}
          mb={defaultSpacing}
          mt={defaultSpacing}
          optionalFilters={store.optional_filters}
          searchProps={searchProps}
        />
      )}
      <MenuProducts category="featured" searchProps={searchProps} />
      <Outlet />
    </>
  );
};
